export type QueueMessageStatus = 'nuevo' | 'favorito' | 'fijado' | 'pendiente' | 'leido_aire' | 'archivado' | 'bloqueado';

export type DetectedType = 'saludo' | 'solicitud_cancion' | 'peticion' | 'concurso' | 'queja' | 'urgente' | 'spam' | 'revision';

export type QueueFilter = 'todos' | 'favoritos' | 'fijados' | 'saludos' | 'canciones' | 'pendientes' | 'leidos_aire' | 'bloqueados' | 'revision';

export interface QueueMessage {
  id: number;
  senderName: string;
  senderAvatar: string;
  content: string;
  timestamp: string;
  queueStatus: QueueMessageStatus;
  detectedType: DetectedType;
  priority: 'normal' | 'alta' | 'urgente';
  ip: string;
  isFavorited: boolean;
  isPinned: boolean;
  readAtAir?: string;
  songRequest?: string;
  voiceDuration?: string;
  contestAnswer?: string;
}

export const detectedTypeLabels: Record<DetectedType, string> = {
  saludo: 'Saludo',
  solicitud_cancion: 'Solicitud de canción',
  peticion: 'Petición',
  concurso: 'Concurso',
  queja: 'Queja',
  urgente: 'Urgente',
  spam: 'Spam',
  revision: 'Revisión',
};

export const detectedTypeColors: Record<DetectedType, { bg: string; text: string; icon: string }> = {
  saludo: { bg: 'bg-emerald-100', text: 'text-emerald-700', icon: 'ri-user-heart-line' },
  solicitud_cancion: { bg: 'bg-violet-100', text: 'text-violet-700', icon: 'ri-music-line' },
  peticion: { bg: 'bg-sky-100', text: 'text-sky-700', icon: 'ri-hand-heart-line' },
  concurso: { bg: 'bg-amber-100', text: 'text-amber-700', icon: 'ri-trophy-line' },
  queja: { bg: 'bg-red-100', text: 'text-red-700', icon: 'ri-error-warning-line' },
  urgente: { bg: 'bg-rose-100', text: 'text-rose-700', icon: 'ri-alert-line' },
  spam: { bg: 'bg-gray-100', text: 'text-gray-600', icon: 'ri-spam-line' },
  revision: { bg: 'bg-orange-100', text: 'text-orange-700', icon: 'ri-search-eye-line' },
};

export const queueStatusLabels: Record<QueueMessageStatus, string> = {
  nuevo: 'Nuevo',
  favorito: 'Favorito',
  fijado: 'Fijado',
  pendiente: 'Pendiente',
  leido_aire: 'Leído al aire',
  archivado: 'Archivado',
  bloqueado: 'Bloqueado',
};

export const queueStatusColors: Record<QueueMessageStatus, { dot: string; text: string; bg: string }> = {
  nuevo: { dot: 'bg-primary-500', text: 'text-primary-700', bg: 'bg-primary-100' },
  favorito: { dot: 'bg-amber-400', text: 'text-amber-700', bg: 'bg-amber-100' },
  fijado: { dot: 'bg-violet-500', text: 'text-violet-700', bg: 'bg-violet-100' },
  pendiente: { dot: 'bg-orange-500', text: 'text-orange-700', bg: 'bg-orange-100' },
  leido_aire: { dot: 'bg-emerald-500', text: 'text-emerald-700', bg: 'bg-emerald-100' },
  archivado: { dot: 'bg-foreground-400', text: 'text-foreground-600', bg: 'bg-foreground-100' },
  bloqueado: { dot: 'bg-red-500', text: 'text-red-700', bg: 'bg-red-100' },
};

export const filterLabels: Record<QueueFilter, string> = {
  todos: 'Todos',
  favoritos: 'Favoritos',
  fijados: 'Fijados',
  saludos: 'Saludos',
  canciones: 'Canciones',
  pendientes: 'Pendientes',
  leidos_aire: 'Leídos al aire',
  bloqueados: 'Bloqueados',
  revision: 'En revisión',
};

export const filterIcons: Record<QueueFilter, string> = {
  todos: 'ri-inbox-line',
  favoritos: 'ri-star-line',
  fijados: 'ri-pushpin-line',
  saludos: 'ri-user-heart-line',
  canciones: 'ri-music-line',
  pendientes: 'ri-time-line',
  leidos_aire: 'ri-broadcast-line',
  bloqueados: 'ri-forbid-2-line',
  revision: 'ri-search-eye-line',
};

/* ─── Keyword-triggered auto-detection rules ─── */
export const autoDetectionRules: { keywords: string[]; type: DetectedType }[] = [
  {
    keywords: ['saludos', 'quiero saludar', 'manda saludos', 'mandar saludos', 'saludar a', 'un saludo', 'saluditos', 'saludazo', 'buenos días', 'buenas tardes', 'buenas noches', 'hola radio', 'hola equipo'],
    type: 'saludo',
  },
  {
    keywords: ['complaceme', 'puedes poner', 'dedica', 'dedicame', 'quiero escuchar', 'pongan', 'me gustaría escuchar', 'podrían poner', 'para dedicar', 'me encantaría escuchar'],
    type: 'solicitud_cancion',
  },
  {
    keywords: ['petición', 'oración', 'pido', 'solicito', 'necesito que', 'por favor ayúdenme'],
    type: 'peticion',
  },
  {
    keywords: ['concurso', 'respuesta del concurso', 'mi respuesta', 'participar en el concurso', 'sorteo', 'trivia'],
    type: 'concurso',
  },
  {
    keywords: ['queja', 'me quejo', 'denuncia', 'reporto', 'mal servicio', 'no me gustó', 'decepcionado', 'molesto', 'pésimo', 'horrible'],
    type: 'queja',
  },
  {
    keywords: ['urgente', 'emergencia', 'ayuda', 'sos', 'por favor urgente', 'necesito ayuda', 'es grave'],
    type: 'urgente',
  },
  {
    keywords: ['compra', 'descuento', 'oferta', 'gana dinero', 'haz clic', 'visita mi', 'whatsapp', 'gratis', 'promoción', 'bit.ly', 't.ly', 'tinyurl'],
    type: 'spam',
  },
];

export function detectMessageType(content: string, existingSongRequest?: string, existingContestAnswer?: string): DetectedType {
  const lower = content.toLowerCase();

  if (existingSongRequest) return 'solicitud_cancion';
  if (existingContestAnswer) return 'concurso';

  for (const rule of autoDetectionRules) {
    for (const kw of rule.keywords) {
      if (lower.includes(kw.toLowerCase())) {
        return rule.type;
      }
    }
  }

  return 'revision';
}

/* ─── Mock Queue Messages ─── */
export const queueMessages: QueueMessage[] = [
  {
    id: 1,
    senderName: 'María Fernanda López',
    senderAvatar: 'https://readdy.ai/api/search-image?query=Portrait%20of%20a%20young%20Latina%20woman%20with%20warm%20smile%2C%20soft%20natural%20lighting%2C%20friendly%20approachable%20expression%2C%20clean%20light%20background%2C%20casual%20style&width=80&height=80&seq=queue-avatar-maria&orientation=squarish',
    content: 'Buenas tardes! Quiero mandar saludos a mi mamá en Texas, la extraño mucho y sé que nos escucha por internet. También para mi hermana que está de cumpleaños hoy. ¡Son las mejores!',
    timestamp: '2026-06-08T20:30:00',
    queueStatus: 'nuevo',
    detectedType: 'saludo',
    priority: 'normal',
    ip: '201.187.92.14',
    isFavorited: false,
    isPinned: false,
  },
  {
    id: 2,
    senderName: 'Carlos Ramírez',
    senderAvatar: 'https://readdy.ai/api/search-image?query=Portrait%20of%20a%20middle%20aged%20Latino%20man%2C%20friendly%20face%2C%20casual%20polo%20shirt%2C%20natural%20daylight%2C%20warm%20expression%2C%20clean%20background&width=80&height=80&seq=queue-avatar-carlos&orientation=squarish',
    content: 'Un saludo enorme para toda la gente de San José que nos escucha desde temprano. ¡Arriba el barrio El Carmen! Y un abrazo especial para mi esposa Andrea que está en el hospital, todo va a salir bien mi amor.',
    timestamp: '2026-06-08T20:12:00',
    queueStatus: 'fijado',
    detectedType: 'saludo',
    priority: 'alta',
    ip: '190.87.45.22',
    isFavorited: true,
    isPinned: true,
  },
  {
    id: 3,
    senderName: 'Andrea Torres',
    senderAvatar: 'https://readdy.ai/api/search-image?query=Portrait%20of%20a%20young%20woman%20with%20bright%20smile%2C%20everyday%20casual%20look%2C%20soft%20daylight%20studio%20lighting%2C%20friendly%20expression%2C%20clean%20light%20background&width=80&height=80&seq=queue-avatar-andrea&orientation=squarish',
    content: 'Para el concurso de las entradas del Summer Fest: la respuesta es "Los Ecos" — la banda que abrió el festival del año pasado.',
    timestamp: '2026-06-08T19:55:00',
    queueStatus: 'nuevo',
    detectedType: 'concurso',
    priority: 'normal',
    ip: '186.32.11.45',
    isFavorited: false,
    isPinned: false,
    contestAnswer: 'Los Ecos',
  },
  {
    id: 4,
    senderName: 'Diego Vargas',
    senderAvatar: 'https://readdy.ai/api/search-image?query=Portrait%20of%20a%20young%20Latino%20man%2C%20stylish%20casual%20look%2C%20confident%20friendly%20expression%2C%20modern%20studio%20lighting%2C%20clean%20warm%20background&width=80&height=80&seq=queue-avatar-diego&orientation=squarish',
    content: 'Amé la entrevista que hicieron a Los Ecos el jueves. Pregúntenles en la próxima si van a sacar nuevo álbum este año, necesito más música de ellos. ¡Son los mejores!',
    timestamp: '2026-06-08T19:30:00',
    queueStatus: 'leido_aire',
    detectedType: 'peticion',
    priority: 'normal',
    ip: '201.189.33.78',
    isFavorited: false,
    isPinned: false,
    readAtAir: '2026-06-08T19:45:00',
  },
  {
    id: 5,
    senderName: 'Lucía Mendoza',
    senderAvatar: 'https://readdy.ai/api/search-image?query=Portrait%20of%20a%20Latina%20woman%20with%20warm%20genuine%20smile%2C%20natural%20makeup%2C%20casual%20look%2C%20soft%20natural%20light%2C%20clean%20bright%20background&width=80&height=80&seq=queue-avatar-lucia&orientation=squarish',
    content: 'Hola! Complaceme con "Vivir Mi Vida" de Marc Anthony, es mi canción favorita y hoy estoy celebrando mi cumpleaños con mi familia. Sería el mejor regalo que me pueden dar!',
    timestamp: '2026-06-08T19:10:00',
    queueStatus: 'nuevo',
    detectedType: 'solicitud_cancion',
    priority: 'normal',
    ip: '186.33.55.90',
    isFavorited: false,
    isPinned: false,
    songRequest: 'Vivir Mi Vida — Marc Anthony',
  },
  {
    id: 6,
    senderName: 'Pedro Gutiérrez',
    senderAvatar: 'https://readdy.ai/api/search-image?query=Portrait%20of%20an%20older%20Latino%20man%20with%20kind%20eyes%2C%20simple%20casual%20style%2C%20natural%20lighting%2C%20warm%20expression%2C%20clean%20background&width=80&height=80&seq=queue-avatar-pedro&orientation=squarish',
    content: 'Para dedicar a mi esposa en nuestro aniversario 25: "Perfect" de Ed Sheeran. Gracias por acompañarnos todos estos años, Radio Frecuencia es parte de nuestra historia juntos.',
    timestamp: '2026-06-08T18:45:00',
    queueStatus: 'leido_aire',
    detectedType: 'solicitud_cancion',
    priority: 'alta',
    ip: '190.14.22.33',
    isFavorited: true,
    isPinned: false,
    songRequest: 'Perfect — Ed Sheeran',
    readAtAir: '2026-06-08T19:00:00',
  },
  {
    id: 7,
    senderName: 'Sofía Hernández',
    senderAvatar: 'https://readdy.ai/api/search-image?query=Portrait%20of%20a%20young%20Latina%20woman%2C%20trendy%20casual%20style%2C%20bright%20genuine%20smile%2C%20soft%20studio%20light%2C%20clean%20background&width=80&height=80&seq=queue-avatar-sofia&orientation=squarish',
    content: 'Buenas! Saludos para todo el equipo de Radio Frecuencia, son los mejores. Especialmente para Natalia que nos alegra las mañanas y a Diego que siempre me hace reír con sus comentarios.',
    timestamp: '2026-06-08T18:20:00',
    queueStatus: 'favorito',
    detectedType: 'saludo',
    priority: 'normal',
    ip: '201.178.44.67',
    isFavorited: true,
    isPinned: false,
  },
  {
    id: 8,
    senderName: 'Roberto Sánchez',
    senderAvatar: 'https://readdy.ai/api/search-image?query=Portrait%20of%20a%20middle%20aged%20Latino%20man%2C%20casual%20style%2C%20friendly%20expression%2C%20natural%20daylight%2C%20clean%20background&width=80&height=80&seq=queue-avatar-roberto&orientation=squarish',
    content: 'Concurso de entradas Summer Fest — respuesta: Fueron 12 bandas en total las que tocaron en el Parque Central durante las 3 noches del festival.',
    timestamp: '2026-06-08T17:55:00',
    queueStatus: 'nuevo',
    detectedType: 'concurso',
    priority: 'normal',
    ip: '187.55.88.12',
    isFavorited: false,
    isPinned: false,
    contestAnswer: '12 bandas',
  },
  {
    id: 9,
    senderName: 'Valentina Castro',
    senderAvatar: 'https://readdy.ai/api/search-image?query=Portrait%20of%20a%20young%20woman%20with%20artistic%20style%2C%20creative%20look%2C%20warm%20smile%2C%20soft%20ambient%20light%2C%20clean%20background&width=80&height=80&seq=queue-avatar-valentina&orientation=squarish',
    content: 'Propongo que hagan un segmento de covers acústicos en vivo los viernes. Los oyentes podemos votar la canción y el locutor la canta con guitarra. Podría llamarse "Viernes de Acústico". ¿Qué opinan?',
    timestamp: '2026-06-08T17:30:00',
    queueStatus: 'pendiente',
    detectedType: 'peticion',
    priority: 'alta',
    ip: '190.92.33.11',
    isFavorited: true,
    isPinned: true,
  },
  {
    id: 10,
    senderName: 'Martín Rojas',
    senderAvatar: 'https://readdy.ai/api/search-image?query=Portrait%20of%20a%20young%20Latino%20man%2C%20urban%20casual%20style%2C%20confident%20expression%2C%20modern%20lighting%2C%20clean%20background&width=80&height=80&seq=queue-avatar-martin&orientation=squarish',
    content: 'VISITA MI CANAL GRATIS de apuestas deportivas, gana dinero fácil con mis predicciones 100% seguras. Haz clic aquí bit.ly/apuestas-seguras. Promoción limitada!!!',
    timestamp: '2026-06-08T17:10:00',
    queueStatus: 'bloqueado',
    detectedType: 'spam',
    priority: 'normal',
    ip: '45.33.128.77',
    isFavorited: false,
    isPinned: false,
  },
  {
    id: 11,
    senderName: 'Ana Patricia Vega',
    senderAvatar: 'https://readdy.ai/api/search-image?query=Portrait%20of%20a%20mature%20Latina%20woman%2C%20elegant%20casual%20style%2C%20warm%20genuine%20smile%2C%20soft%20natural%20light%2C%20clean%20background&width=80&height=80&seq=queue-avatar-ana&orientation=squarish',
    content: 'Para la hora del café, ¿podrían poner "Ojalá" de Silvio Rodríguez? Es una canción que me conecta con mis raíces y siempre me hace reflexionar. Gracias infinitas.',
    timestamp: '2026-06-08T16:50:00',
    queueStatus: 'leido_aire',
    detectedType: 'solicitud_cancion',
    priority: 'normal',
    ip: '186.45.77.30',
    isFavorited: false,
    isPinned: false,
    songRequest: 'Ojalá — Silvio Rodríguez',
    readAtAir: '2026-06-08T17:15:00',
  },
  {
    id: 12,
    senderName: 'Francisco Jiménez',
    senderAvatar: 'https://readdy.ai/api/search-image?query=Portrait%20of%20an%20older%20Latino%20man%2C%20kind%20weathered%20face%2C%20simple%20style%2C%20natural%20lighting%2C%20warm%20expression%2C%20clean%20background&width=80&height=80&seq=queue-avatar-francisco&orientation=squarish',
    content: 'Saludos desde Heredia para todos los radioescuchas de la 98.7 FM. Sintonizándolos desde hace 15 años, el mejor acompañamiento para mis mañanas en el taller. ¡Pura vida!',
    timestamp: '2026-06-08T16:25:00',
    queueStatus: 'bloqueado',
    detectedType: 'saludo',
    priority: 'normal',
    ip: '201.202.55.44',
    isFavorited: false,
    isPinned: false,
  },
  {
    id: 13,
    senderName: 'Gabriela Mora',
    senderAvatar: 'https://readdy.ai/api/search-image?query=Portrait%20of%20a%20young%20professional%20Latina%20woman%2C%20business%20casual%2C%20confident%20smile%2C%20soft%20studio%20light%2C%20clean%20background&width=80&height=80&seq=queue-avatar-gabriela&orientation=squarish',
    content: 'Hola equipo! Soy organizadora de eventos corporativos y me encantaría saber si Radio Frecuencia hace transmisiones en vivo para eventos privados. Tenemos una feria empresarial en agosto.',
    timestamp: '2026-06-08T15:55:00',
    queueStatus: 'pendiente',
    detectedType: 'peticion',
    priority: 'alta',
    ip: '190.56.11.88',
    isFavorited: true,
    isPinned: false,
  },
  {
    id: 14,
    senderName: 'Javier Núñez',
    senderAvatar: 'https://readdy.ai/api/search-image?query=Portrait%20of%20a%20young%20Latino%20man%2C%20trendy%20street%20style%2C%20charismatic%20smile%2C%20urban%20studio%20lighting%2C%20clean%20background&width=80&height=80&seq=queue-avatar-javier&orientation=squarish',
    content: 'URGENTE: Hay un accidente en la ruta 27 a la altura de Escazú, mucho tráfico. Por favor avisen a los radioescuchas para que tomen rutas alternas. ¡Gracias!',
    timestamp: '2026-06-08T15:40:00',
    queueStatus: 'fijado',
    detectedType: 'urgente',
    priority: 'urgente',
    ip: '201.177.33.55',
    isFavorited: false,
    isPinned: true,
  },
  {
    id: 15,
    senderName: 'Natalia Campos',
    senderAvatar: 'https://readdy.ai/api/search-image?query=Portrait%20of%20a%20Latina%20woman%20in%20her%2030s%2C%20warm%20friendly%20smile%2C%20casual%20chic%20style%2C%20soft%20natural%20daylight%2C%20clean%20background&width=80&height=80&seq=queue-avatar-natalia&orientation=squarish',
    content: 'Buenos días equipo! Saludos a toda la gente linda que nos escucha desde temprano. Hoy amanecí con una energía increíble y quería compartirla con todos ustedes. ¡Bendiciones!',
    timestamp: '2026-06-08T15:10:00',
    queueStatus: 'nuevo',
    detectedType: 'saludo',
    priority: 'normal',
    ip: '186.77.44.21',
    isFavorited: false,
    isPinned: false,
  },
  {
    id: 16,
    senderName: 'Eduardo Paredes',
    senderAvatar: 'https://readdy.ai/api/search-image?query=Portrait%20of%20a%20middle%20aged%20Latino%20man%2C%20casual%20professional%20style%2C%20friendly%20but%20serious%20expression%2C%20studio%20lighting%2C%20clean%20background&width=80&height=80&seq=queue-avatar-eduardo&orientation=squarish',
    content: 'Reporto que ayer en la transmisión del partido hubo cortes de audio cada 5 minutos. No sé si fue solo en mi zona o problema general. Vivo en Cartago.',
    timestamp: '2026-06-08T14:40:00',
    queueStatus: 'pendiente',
    detectedType: 'queja',
    priority: 'alta',
    ip: '190.64.22.10',
    isFavorited: false,
    isPinned: false,
  },
  {
    id: 17,
    senderName: 'Isabel Ramírez',
    senderAvatar: 'https://readdy.ai/api/search-image?query=Portrait%20of%20a%20young%20teen%20Latina%20girl%2C%20bright%20cheerful%20expression%2C%20casual%20style%2C%20colorful%20background%2C%20natural%20light&width=80&height=80&seq=queue-avatar-isabel&orientation=squarish',
    content: 'Holaaa! Me gustaría escuchar "Flowers" de Miley Cyrus. Es nuestro himno con mi mejor amiga que está de cumple! Somos fans #1 de Radio Frecuencia.',
    timestamp: '2026-06-08T14:15:00',
    queueStatus: 'nuevo',
    detectedType: 'solicitud_cancion',
    priority: 'normal',
    ip: '201.185.90.33',
    isFavorited: false,
    isPinned: false,
    songRequest: 'Flowers — Miley Cyrus',
  },
  {
    id: 18,
    senderName: 'Tomás Blanco',
    senderAvatar: 'https://readdy.ai/api/search-image?query=Portrait%20of%20a%20young%20European%20Latino%20mixed%20man%2C%20creative%20style%2C%20thoughtful%20expression%2C%20soft%20studio%20light%2C%20clean%20background&width=80&height=80&seq=queue-avatar-tomas&orientation=squarish',
    content: 'Desde Alajuela mandando un abrazo a todos los escuchas de la noche. Ustedes hacen que manejar de vuelta a casa sea mucho más llevadero. ¡Temazos los de hoy!',
    timestamp: '2026-06-08T13:50:00',
    queueStatus: 'leido_aire',
    detectedType: 'saludo',
    priority: 'normal',
    ip: '187.26.55.77',
    isFavorited: false,
    isPinned: false,
    readAtAir: '2026-06-08T14:10:00',
  },
  {
    id: 19,
    senderName: 'Daniela Quirós',
    senderAvatar: 'https://readdy.ai/api/search-image?query=Portrait%20of%20a%20young%20fit%20Latina%20woman%2C%20sporty%20casual%20look%2C%20energetic%20smile%2C%20bright%20natural%20light%2C%20clean%20background&width=80&height=80&seq=queue-avatar-daniela&orientation=squarish',
    content: 'Concurso Summer Fest — El headliner de la segunda noche fue DJ Phantom con su set de electrónica latina que duró casi 2 horas. ¡Increíble energía!',
    timestamp: '2026-06-08T13:20:00',
    queueStatus: 'nuevo',
    detectedType: 'concurso',
    priority: 'normal',
    ip: '190.88.33.99',
    isFavorited: false,
    isPinned: false,
    contestAnswer: 'DJ Phantom',
  },
  {
    id: 20,
    senderName: 'Ricardo Alfaro',
    senderAvatar: 'https://readdy.ai/api/search-image?query=Portrait%20of%20a%20middle%20aged%20Latino%20man%2C%20warm%20smile%2C%20casual%20professional%20look%2C%20natural%20daylight%2C%20clean%20background&width=80&height=80&seq=queue-avatar-ricardo&orientation=squarish',
    content: 'Soy profe de comunicación en la UCR. Necesito que hagan visitas guiadas a la cabina para grupos de estudiantes. Sería una experiencia increíble para mis alumnos de producción radiofónica.',
    timestamp: '2026-06-08T12:55:00',
    queueStatus: 'pendiente',
    detectedType: 'peticion',
    priority: 'normal',
    ip: '201.166.44.22',
    isFavorited: false,
    isPinned: false,
  },
  {
    id: 21,
    senderName: 'Paula Montero',
    senderAvatar: 'https://readdy.ai/api/search-image?query=Portrait%20of%20a%20young%20Latina%20woman%2C%20artistic%20bohemian%20style%2C%20warm%20gentle%20smile%2C%20soft%20ambient%20light%2C%20clean%20background&width=80&height=80&seq=queue-avatar-paula&orientation=squarish',
    content: 'Por favor ayúdenme a encontrar a mi perrito perdido en San Pedro. Se llama Max, es un golden retriever con collar rojo. Ofrezco recompensa. Mi número es 8888-7777.',
    timestamp: '2026-06-08T12:30:00',
    queueStatus: 'fijado',
    detectedType: 'urgente',
    priority: 'urgente',
    ip: '186.44.99.55',
    isFavorited: true,
    isPinned: true,
  },
  {
    id: 22,
    senderName: 'Mario Castillo',
    senderAvatar: 'https://readdy.ai/api/search-image?query=Portrait%20of%20an%20older%20Latino%20man%2C%20distinguished%20look%2C%20kind%20eyes%2C%20simple%20elegant%20style%2C%20classic%20studio%20lighting%2C%20clean%20background&width=80&height=80&seq=queue-avatar-mario&orientation=squarish',
    content: 'Para recordar viejos tiempos: "Hotel California" de Eagles. Esta canción marcó mi juventud y cada vez que suena en su estación me transporta. Gracias por mantener viva la buena música.',
    timestamp: '2026-06-08T12:05:00',
    queueStatus: 'favorito',
    detectedType: 'solicitud_cancion',
    priority: 'normal',
    ip: '190.112.77.33',
    isFavorited: true,
    isPinned: false,
    songRequest: 'Hotel California — Eagles',
  },
  {
    id: 23,
    senderName: 'Camila Soto',
    senderAvatar: 'https://readdy.ai/api/search-image?query=Portrait%20of%20a%20young%20Latina%20woman%2C%20modern%20trendy%20style%2C%20bright%20genuine%20smile%2C%20natural%20daylight%2C%20clean%20background&width=80&height=80&seq=queue-avatar-camila&orientation=squarish',
    content: 'Buen díaaaas! Saludos al equipo de Tardes de Pop, me acompañan en el gym todas las tardes y hacen mi rutina mucho más divertida. ¡Son lo máximo!',
    timestamp: '2026-06-08T11:40:00',
    queueStatus: 'leido_aire',
    detectedType: 'saludo',
    priority: 'normal',
    ip: '201.155.66.44',
    isFavorited: false,
    isPinned: false,
    readAtAir: '2026-06-08T12:00:00',
  },
  {
    id: 24,
    senderName: 'Fernando Leiva',
    senderAvatar: 'https://readdy.ai/api/search-image?query=Portrait%20of%20a%20middle%20aged%20Latino%20man%2C%20casual%20simple%20style%2C%20friendly%20average%20expression%2C%20natural%20lighting%2C%20clean%20background&width=80&height=80&seq=queue-avatar-fernando&orientation=squarish',
    content: 'Estoy muy molesto con la programación de anoche. Pésimo el cambio de horario del programa deportivo sin avisar. Llevo 10 años escuchando y nunca había estado tan decepcionado.',
    timestamp: '2026-06-08T11:10:00',
    queueStatus: 'pendiente',
    detectedType: 'queja',
    priority: 'alta',
    ip: '187.99.55.88',
    isFavorited: false,
    isPinned: false,
  },
  {
    id: 25,
    senderName: 'Jimena Rivas',
    senderAvatar: 'https://readdy.ai/api/search-image?query=Portrait%20of%20a%20young%20Latina%20woman%2C%20fresh%20natural%20look%2C%20warm%20smile%2C%20bright%20morning%20light%2C%20clean%20background&width=80&height=80&seq=queue-avatar-jimena&orientation=squarish',
    content: 'Concurso Summer Fest — El festival fue en el Parque Central de San José, y la entrada era completamente gratis para todo el público. ¡Qué buen evento!',
    timestamp: '2026-06-08T10:45:00',
    queueStatus: 'nuevo',
    detectedType: 'concurso',
    priority: 'normal',
    ip: '190.77.33.22',
    isFavorited: false,
    isPinned: false,
    contestAnswer: 'Parque Central, gratis',
  },
  {
    id: 26,
    senderName: 'Oscar Duarte',
    senderAvatar: 'https://readdy.ai/api/search-image?query=Portrait%20of%20a%20mature%20Latino%20man%2C%20rugged%20friendly%20face%2C%20casual%20workwear%20style%2C%20natural%20outdoor%20lighting%2C%20clean%20warm%20background&width=80&height=80&seq=queue-avatar-oscar&orientation=squarish',
    content: 'Petición especial: mi hija está en el hospital y hoy es su cumpleaños. ¿Podrían dedicarle una canción de Taylor Swift? Es su artista favorita y sería un momento muy especial para ella.',
    timestamp: '2026-06-08T10:20:00',
    queueStatus: 'favorito',
    detectedType: 'peticion',
    priority: 'urgente',
    ip: '201.188.44.66',
    isFavorited: true,
    isPinned: false,
  },
  {
    id: 27,
    senderName: 'SpamBot_2024',
    senderAvatar: 'https://readdy.ai/api/search-image?query=Generic%20default%20avatar%20placeholder%2C%20plain%20simple%20icon%20style%2C%20neutral%20expression%2C%20clean%20minimal%20background&width=80&height=80&seq=queue-avatar-spam&orientation=squarish',
    content: '¡¡¡OFERTA LIMITADA!!! Compra ahora nuestro producto milagroso para bajar de peso en 7 días. 50% descuento solo hoy. Visita bit.ly/milagro-peso. ¡No te lo pierdas!',
    timestamp: '2026-06-08T09:55:00',
    queueStatus: 'bloqueado',
    detectedType: 'spam',
    priority: 'normal',
    ip: '91.234.55.12',
    isFavorited: false,
    isPinned: false,
  },
  {
    id: 28,
    senderName: 'Laura Espinoza',
    senderAvatar: 'https://readdy.ai/api/search-image?query=Portrait%20of%20a%20young%20Latina%20woman%2C%20serene%20calm%20expression%2C%20cozy%20casual%20style%2C%20soft%20warm%20lighting%2C%20clean%20background&width=80&height=80&seq=queue-avatar-laura&orientation=squarish',
    content: 'Buenas noches. Quiero pedir una oración por mi abuelito que está delicado de salud. Agradecería que la comunidad de Radio Frecuencia lo tenga en sus pensamientos esta noche.',
    timestamp: '2026-06-08T09:30:00',
    queueStatus: 'favorito',
    detectedType: 'peticion',
    priority: 'alta',
    ip: '186.55.88.11',
    isFavorited: true,
    isPinned: false,
  },
  {
    id: 29,
    senderName: 'Adrián Chaves',
    senderAvatar: 'https://readdy.ai/api/search-image?query=Portrait%20of%20a%20young%20Latino%20man%2C%20casual%20urban%20style%2C%20relaxed%20smile%2C%20natural%20daylight%2C%20clean%20background&width=80&height=80&seq=queue-avatar-adrian&orientation=squarish',
    content: 'Hola. Simple: ¿me complacen con "Bohemian Rhapsody" de Queen? Es un temazo y hace rato no lo ponen. Sería épico escucharlo camino a casa.',
    timestamp: '2026-06-08T09:05:00',
    queueStatus: 'nuevo',
    detectedType: 'solicitud_cancion',
    priority: 'normal',
    ip: '190.33.77.45',
    isFavorited: false,
    isPinned: false,
    songRequest: 'Bohemian Rhapsody — Queen',
  },
  {
    id: 30,
    senderName: 'Rebeca Solano',
    senderAvatar: 'https://readdy.ai/api/search-image?query=Portrait%20of%20a%20young%20professional%20woman%2C%20modern%20corporate%20casual%2C%20confident%20warm%20smile%2C%20clean%20studio%20background&width=80&height=80&seq=queue-avatar-rebeca&orientation=squarish',
    content: 'Solicito información para patrocinar un segmento matutino. Tengo una cadena de cafeterías y creo que su audiencia es perfecta para nuestra marca. ¿A qué correo puedo escribir?',
    timestamp: '2026-06-08T08:40:00',
    queueStatus: 'pendiente',
    detectedType: 'peticion',
    priority: 'alta',
    ip: '201.190.55.33',
    isFavorited: false,
    isPinned: false,
  },
];